import { TextInput } from '../../../components/FormUtils/TextInput'
import { useAuth } from 'context/authentication/useAuth'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import to from 'await-to-js'
import { handleFormError } from '../../../utils/handleFormError'
import { ErrorText } from '../../../components/FormUtils/ErrorText'
import FbButton from '../../../components/FbUI/FbButton'
import { FbLink } from 'components/FbUI/FbLink'
import { publicFeatureFlags } from '../../../utils/publicFeatureFlags'
import { useEffect, useState } from 'react'

const loginFormShape = z.object({
  email: z.string().email(),
  password: z.string(),
  non_field_errors: z.string().optional(),
})

function Login() {
  const { login, resendVerify } = useAuth()
  const [enableSSO, setEnableSSO] = useState(false)

  useEffect(() => {
    // Check if SSO is enabled using public feature flags
    const checkSSO = async () => {
      await publicFeatureFlags.loadFlags()
      const ssoEnabled = publicFeatureFlags.isEnabled('enable_sso')
      setEnableSSO(ssoEnabled)
    }
    checkSSO()
  }, [])

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors },
    setError,
    getValues,
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
      non_field_errors: '',
    },
    resolver: zodResolver(loginFormShape),
  })

  const onSubmit = handleSubmit(
    async (values) => {
      const [err] = await to(login(values.email, values.password))
      handleFormError(err, setError)
    },
    (errors) => console.warn(errors)
  )

  const resendVerification = async () => {
    const [err] = await to(resendVerify(getValues()?.email))
    handleFormError(err, setError)
  }

  return (
    <form onSubmit={onSubmit} className="w-96 flex-shrink-0">
      <div className="text-[22px] font-light text-new-company-gray-500">
        Welcome to{' '}
        <span className="text-new-company-green-dark font-semibold">
          First Bite
        </span>
      </div>
      <h1 className="text-[18px] mb-2 mt-4">Log In</h1>
      <div className="mb-4">
        {"Don't have an account yet? "}
        <FbLink to="/signup">Get Started</FbLink>
      </div>
      <TextInput control={control} name="email" label="Email" />
      <TextInput
        type="password"
        control={control}
        name="password"
        label="Password"
        className="mb-2"
      />
      <FbLink to="/reset-password">Forgot Password?</FbLink>
      <FbButton className="w-full mt-4" type="submit" loading={isSubmitting}>
        Log In
      </FbButton>

      {errors?.non_field_errors && (
        <ErrorText
          resendVerification={resendVerification}
          control={control}
          name={'non_field_errors'}
        />
      )}

      {enableSSO && (
        <>
          <div className="flex items-center my-4">
            <div className="flex-1 h-px bg-gray-300"></div>
            <span className="px-3 text-gray-600 text-xs">or</span>
            <div className="flex-1 h-px bg-gray-300"></div>
          </div>

          <div className="flex justify-center mt-4">
            <FbLink to="/sso-login" className="w-full no-underline">
              <button
                type="button"
                className="w-full px-4 py-2.5 bg-gray-100 border border-gray-300 rounded text-sm text-gray-800 cursor-pointer transition-colors duration-200 hover:bg-gray-200"
              >
                Sign in with SSO
              </button>
            </FbLink>
          </div>
        </>
      )}
    </form>
  )
}

export default Login
